import { useEffect } from 'react';
import { LoadingState, ErrorState } from '../../components/LoadingState';
import { useGenerateDiscover } from '../../api/queries';
import styles from './DiscoverView.module.css';

// Shape of the /activity/discover response, as observed in the legacy
// widget. All blocks are optional — render only those that come back
// with data so the view degrades gracefully.
type Discover = {
  overview?: { summary?: string; core_benefits?: string };
  knowledge_areas?: Array<{
    area?: string;
    key_concepts?: string;
    learning_progression?: string;
  }>;
  learning_journey?: {
    milestones?: string[] | string;
    progression_path?: string;
    final_capabilities?: string;
  };
  practical_outcomes?: {
    career_relevance?: string;
    real_world_applications?: Array<{ context?: string; example?: string }>;
  };
  skills_development?: Array<{
    skill?: string;
    application?: string;
    mastery_indicators?: string;
  }>;
};

const toList = (v: string[] | string | undefined): string[] => {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  return v.split(',').map((s) => s.trim()).filter(Boolean);
};

export function DiscoverView() {
  const mut = useGenerateDiscover();
  useEffect(() => {
    if (!mut.data && !mut.isPending && !mut.error) mut.mutate({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (mut.isPending)
    return <LoadingState label="Descubriendo el curso…" hint="Resumiendo objetivos, áreas y resultados" />;
  if (mut.error) return <ErrorState error={mut.error} onRetry={() => mut.mutate({})} />;
  const data = mut.data as any;
  if (!data) return null;

  const d: Discover =
    data.content?.course_learning ?? data.content ?? {};
  const title: string = data.title ?? data.content?.title ?? '';

  const journeyMilestones = toList(d.learning_journey?.milestones);

  return (
    <div className={styles.view}>
      {title && <h2 className={styles.title}>{title}</h2>}

      {(d.overview?.core_benefits || d.overview?.summary) && (
        <section className={styles.heroBox}>
          {d.overview?.core_benefits && (
            <div>
              <h3 className={styles.heroLabel}>Por qué te interesa</h3>
              <p>{d.overview.core_benefits}</p>
            </div>
          )}
          {d.overview?.summary && (
            <div>
              <h3 className={styles.heroLabel}>Qué vas a aprender</h3>
              <p>{d.overview.summary}</p>
            </div>
          )}
        </section>
      )}

      {(journeyMilestones.length ||
        d.learning_journey?.progression_path ||
        d.learning_journey?.final_capabilities) && (
        <Section title="Itinerario de aprendizaje">
          {journeyMilestones.length > 0 && (
            <Block label="Hitos">
              <ul className={styles.bullets}>
                {journeyMilestones.map((m, i) => (
                  <li key={i}>{m}</li>
                ))}
              </ul>
            </Block>
          )}
          {d.learning_journey?.progression_path && (
            <Block label="Camino">{d.learning_journey.progression_path}</Block>
          )}
          {d.learning_journey?.final_capabilities && (
            <Block label="Capacidades finales">
              {d.learning_journey.final_capabilities}
            </Block>
          )}
        </Section>
      )}

      {Array.isArray(d.knowledge_areas) && d.knowledge_areas.length > 0 && (
        <Section title="Áreas de conocimiento">
          <div className={styles.cards}>
            {d.knowledge_areas.map((a, i) => (
              <article key={i} className={styles.card}>
                <h4>{a.area}</h4>
                {a.learning_progression && (
                  <p>
                    <strong>Progresión.</strong> {a.learning_progression}
                  </p>
                )}
                {a.key_concepts && (
                  <p>
                    <strong>Conceptos clave.</strong> {a.key_concepts}
                  </p>
                )}
              </article>
            ))}
          </div>
        </Section>
      )}

      {d.practical_outcomes &&
        (d.practical_outcomes.career_relevance ||
          (d.practical_outcomes.real_world_applications?.length ?? 0) > 0) && (
          <Section title="Aplicaciones reales">
            {d.practical_outcomes.career_relevance && (
              <p className={styles.lead}>{d.practical_outcomes.career_relevance}</p>
            )}
            {(d.practical_outcomes.real_world_applications?.length ?? 0) > 0 && (
              <div className={styles.cards}>
                {d.practical_outcomes.real_world_applications!.map((o, i) => (
                  <article key={i} className={styles.card}>
                    <h4>{o.context}</h4>
                    {o.example && <p>{o.example}</p>}
                  </article>
                ))}
              </div>
            )}
          </Section>
        )}

      {Array.isArray(d.skills_development) && d.skills_development.length > 0 && (
        <Section title="Habilidades que desarrollarás">
          <div className={styles.cards}>
            {d.skills_development.map((s, i) => (
              <article key={i} className={styles.card}>
                <h4>{s.skill}</h4>
                {s.application && (
                  <p>
                    <strong>Aplicación.</strong> {s.application}
                  </p>
                )}
                {s.mastery_indicators && (
                  <p>
                    <strong>Cómo sabrás que la dominas.</strong>{' '}
                    {s.mastery_indicators}
                  </p>
                )}
              </article>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

function Section({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className={styles.section}>
      <h3 className={styles.sectionLabel}>{title}</h3>
      {children}
    </section>
  );
}

function Block({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className={styles.block}>
      <span className={styles.blockLabel}>{label}</span>
      <div className={styles.blockBody}>{children}</div>
    </div>
  );
}
